export function createPageUrl(pageName: string) {
    return '/' + pageName.replace(/ /g, '-');
}

/**
 * Format a phone number for display with spaces.
 * Gambian numbers: +220 XXXXX XXXX (9-digit, current) or +220 XXX XXXX (7-digit, legacy)
 * Other numbers: groups of 3-4 digits after country code.
 *
 * The Gambia moved to 9-digit national numbers in 2026. Both lengths are handled because
 * numbers stored before the change are still in the database and still in circulation —
 * without the 9-digit branch they fall through to the generic grouping below and render
 * as "+220 872 047278".
 */
export function formatPhone(phone: string | null | undefined): string {
    if (!phone) return "";
    const digits = phone.replace(/\D/g, "");
    // Gambian number: 220 + 9 digits (current)
    if (digits.startsWith("220") && digits.length === 12) {
        return `+220 ${digits.slice(3, 8)} ${digits.slice(8)}`;
    }
    // Gambian number: 220 + 7 digits (legacy, pre-2026)
    if (digits.startsWith("220") && digits.length === 10) {
        return `+220 ${digits.slice(3, 6)} ${digits.slice(6)}`;
    }
    // Already has + prefix — split off a country code we actually recognise.
    //
    // The previous implementation matched /^(\d{1,3})(\d{3})(\d+)$/, and because \d{1,3} is
    // greedy it always consumed three digits as the country code. A US number therefore
    // rendered as "+164 123 32427" instead of "+1 641 233 2427". Country-code lengths vary
    // (1-3 digits) and cannot be inferred from the number alone, so match against a known
    // list — longest first, so "220" wins over "22" and "2" — and leave anything unrecognised
    // untouched rather than inventing a split.
    if (phone.startsWith("+")) {
        const code = COUNTRY_CODES.find((c) => digits.startsWith(c));
        if (code) {
            const rest = digits.slice(code.length);
            if (rest.length >= 4) return `+${code} ${groupDigits(rest)}`;
        }
    }
    // Unrecognised country code — return as entered rather than mis-grouping it
    return phone;
}

/** Country codes for The Gambia and its main diaspora markets, longest first. */
const COUNTRY_CODES = ["220", "221", "351", "353", "44", "34", "46", "47", "49", "31", "32", "39", "33", "1"];

/** Group digits in threes, merging a trailing 1-2 digit orphan into the previous group. */
function groupDigits(rest: string): string {
    const groups: string[] = [];
    for (let i = 0; i < rest.length; i += 3) groups.push(rest.slice(i, i + 3));
    if (groups.length > 1 && groups[groups.length - 1].length < 3) {
        groups[groups.length - 2] += groups.pop();
    }
    return groups.join(" ");
}

/**
 * Format an ISO date string (e.g. "2026-04-29") to "29 Apr 2026".
 */
export function formatDate(date: string | null | undefined): string {
    if (!date) return "";
    try {
        const d = new Date(date + (date.includes("T") ? "" : "T00:00:00"));
        return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch { return date; }
}

/**
 * Format a time string (e.g. "10:30:00" or "10:30") to "10:30 AM".
 */
/**
 * Strip tracking parameters from social media URLs.
 * e.g. https://www.tiktok.com/@user?fbclid=xxx&_r=1 → https://www.tiktok.com/@user
 */
export function cleanSocialUrl(url: string | null | undefined): string | undefined {
    if (!url) return undefined;
    try {
        const u = new URL(url.startsWith("http") ? url : `https://${url}`);
        // YouTube — keep the video ID param (v=), strip tracking params
        if (/youtube\.com/.test(u.hostname)) {
            const v = u.searchParams.get("v");
            const base = `${u.origin}${u.pathname}`.replace(/\/+$/, "");
            return v ? `${base}?v=${v}` : base;
        }
        // youtu.be short links — path IS the video ID, no params needed
        if (/youtu\.be/.test(u.hostname)) {
            return `${u.origin}${u.pathname}`.replace(/\/+$/, "");
        }
        // Facebook — keep the profile id (profile.php?id=…) for accounts with no
        // vanity URL; without it, facebook.com/profile.php resolves to the viewer's
        // own profile. Other (tracking) params are still dropped.
        if (/facebook\.com|fb\.com/.test(u.hostname)) {
            const base = `${u.origin}${u.pathname}`.replace(/\/+$/, "");
            const id = u.searchParams.get("id");
            return id ? `${base}?id=${id}` : base;
        }
        // TikTok, Instagram — strip all query params (no meaningful id-style params)
        if (/tiktok\.com|instagram\.com/.test(u.hostname)) {
            return `${u.origin}${u.pathname}`.replace(/\/+$/, "");
        }
        return url;
    } catch {
        return url;
    }
}

export function formatTime(time: string | null | undefined): string {
    if (!time) return "";
    const [h, m] = time.split(":").map(Number);
    if (isNaN(h) || isNaN(m)) return time;
    const ampm = h >= 12 ? "PM" : "AM";
    const hour = h % 12 || 12;
    return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
}